import cx from 'classnames';
import { useMemo } from 'react';

import { Button, Icon } from '@sb/design-system';

import { ChatHistoryRow } from './ChatHistoryRow';
import type { ChatHistorySession } from './ChatHistorySession';

export interface ChatHistoryViewProps {
  /**
   * Sessions to render. The view sorts by `lastUpdatedAt` desc, so
   * callers don't have to pre-sort. An empty list renders the empty
   * state instead of the rows.
   */
  sessions: ChatHistorySession[];
  /** Session id currently bound in the panel, or null. Drives the active dot. */
  activeSessionID: string | null;
  /** Fires when the user picks Load on a row. */
  onLoad: (sessionID: string) => void;
  /** Fires when the user commits a rename on a row. */
  onRename: (sessionID: string, newTitle: string) => void;
  /**
   * Fires when the user picks Delete on a row. The view does not own
   * the confirm modal — the parent renders `DeleteSessionModal` and
   * wires `confirm` / `cancel` to the persistence layer.
   */
  onRequestDelete: (sessionID: string) => void;
  /**
   * Non-null when the live subscription backing this list has failed
   * and given up retrying. The list stops updating at that point, so
   * the operator is told rather than left looking at a silently frozen
   * (or empty) list.
   */
  loadError?: Error | null;
  /** Reopens the failed subscription. Rendered as Retry. */
  onRetryLoad?: () => void;
  /**
   * Non-null when the last rename or delete failed. Unlike `loadError`
   * there is no Retry — the operator redoes the action.
   */
  operationError?: Error | null;
}

/**
 * Chat-history list. The list contents depend on the caller's
 * filtering: when invoked on a routine page, `sessions` should
 * already be filtered to that `routineID`; on a non-routine page,
 * `sessions` is the global cross-routine list. The view itself is
 * filter-agnostic and just renders whatever it's handed.
 *
 * Routine-deleted rows (where `session.routineID === null`) stay
 * visible with the "Routine deleted: <name>" subtitle and a
 * disabled Load action.
 */
export function ChatHistoryView({
  sessions,
  activeSessionID,
  onLoad,
  onRename,
  onRequestDelete,
  loadError = null,
  onRetryLoad,
  operationError = null,
}: ChatHistoryViewProps) {
  const sorted = useMemo(
    () => [...sessions].sort((a, b) => b.lastUpdatedAt - a.lastUpdatedAt),
    [sessions],
  );

  if (loadError !== null && sorted.length === 0) {
    return (
      <div
        data-testid="chat-history-load-error"
        role="alert"
        className={cx(
          'tw-flex',
          'tw-flex-1',
          'tw-flex-col',
          'tw-items-center',
          'tw-justify-center',
          'tw-py-40',
          'tw-px-24',
          'tw-gap-12',
          'tw-text-center',
        )}
      >
        <div
          className="tw-text-13 tw-font-medium"
          style={{ color: 'var(--label-primary)' }}
        >
          Couldn&apos;t load your chats
        </div>
        <div
          className="tw-text-13"
          style={{ maxWidth: 260, color: 'var(--label-secondary)' }}
        >
          {loadError.message}
        </div>
        {onRetryLoad ? (
          <Button
            type="button"
            variant="Gray"
            size={36}
            onClick={onRetryLoad}
            data-testid="chat-history-load-error-retry-button"
          >
            Retry
          </Button>
        ) : null}
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <div
        className={cx(
          'tw-flex',
          'tw-flex-1',
          'tw-flex-col',
          'tw-items-center',
          'tw-justify-center',
          'tw-py-40',
          'tw-px-24',
          'tw-text-center',
        )}
        // 14px gap isn't in the spacing scale (12 → 16).
        style={{ gap: 14 }}
      >
        <div
          className={cx(
            'tw-flex',
            'tw-items-center',
            'tw-justify-center',
            'tw-w-44',
            'tw-h-44',
            'tw-rounded-full',
          )}
          style={{ background: 'var(--fill-primary)' }}
        >
          <Icon
            kind="clock"
            className="tw-icon-22"
            style={{ color: 'var(--label-secondary)' }}
          />
        </div>
        <div
          className="tw-text-13 tw-font-medium"
          style={{ color: 'var(--label-primary)' }}
        >
          No chats yet
        </div>
        <div
          className="tw-text-13"
          style={{
            // 260px max-width isn't in the maxWidth scale (256 → 320).
            maxWidth: 260,
            color: 'var(--label-secondary)',
          }}
        >
          Past conversations will appear here. Start a new chat from the menu to
          create one.
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx(
        'tw-flex',
        'tw-flex-1',
        'tw-flex-col',
        'tw-min-h-0',
        'tw-gap-2',
        'tw-overflow-y-auto',
        'tw-px-8',
        'tw-py-8',
      )}
    >
      {operationError !== null ? (
        <div
          data-testid="chat-history-operation-error"
          role="alert"
          className={cx('tw-px-8', 'tw-py-8', 'tw-text-13')}
          style={{ color: 'var(--label-secondary)' }}
        >
          That didn&apos;t work: {operationError.message}
        </div>
      ) : null}
      {loadError !== null ? (
        <div
          data-testid="chat-history-load-error"
          role="alert"
          className={cx(
            'tw-flex',
            'tw-items-center',
            'tw-justify-between',
            'tw-gap-8',
            'tw-px-8',
            'tw-py-8',
            'tw-text-13',
          )}
          style={{ color: 'var(--label-secondary)' }}
        >
          {/* Rows are already on screen, so this stays a banner rather
              than replacing them — stale chats are still more useful
              than nothing while the list is not updating. */}
          <span>This list stopped updating.</span>
          {onRetryLoad ? (
            <Button
              type="button"
              variant="Gray"
              size={32}
              onClick={onRetryLoad}
              data-testid="chat-history-load-error-retry-button"
            >
              Retry
            </Button>
          ) : null}
        </div>
      ) : null}
      {sorted.map((session) => (
        <ChatHistoryRow
          key={session.id}
          session={session}
          isActive={session.id === activeSessionID}
          onLoad={() => onLoad(session.id)}
          onRename={(newTitle) => onRename(session.id, newTitle)}
          onRequestDelete={() => onRequestDelete(session.id)}
        />
      ))}
    </div>
  );
}
